import React, { useContext, useEffect, useState } from "react";
import i18next from "i18next";
import { Box, Divider, Drawer, IconButton, ListItemIcon, Menu, MenuItem, Select, Typography } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "tss-react/mui";
import { useHistory, useLocation } from "react-router-dom";
import { Context } from "states/Store";
import ROUTES, { getUrlWithAccountId } from "config/Routes";
import { setAppMode } from "config/AppMode";
import useSelectedAccountId from "components/common/hooks/useSelectedAccountId";
import EASnackBar from "components/EASnackBar";
import LightLoader from "./LightLoader";
import { useAuth } from "@ea-webapp/auth";
import { RoleBasedAccessControlContext, PrivilegeEnum } from "services/common/RolesUtils";
import { getContextModel } from "services/edge/ContexteService";
import { MEDIA } from "./lightTheme";

// ─── Brand ────────────────────────────────────────────────────────────────────
const BRAND = {
  primary:    "#003A22",
  secondary:  "#54CC54",
  background: "#F6F9F3",
  textMain:   "#1D2939",
  textMuted:  "#475467",
  border:     "#E5E5E5",
  white:      "#ffffff",
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const useStyles = makeStyles()(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: BRAND.background,
    fontFamily: "Inter, sans-serif",
  },

  // ── Navbar ──
  navbar: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1200,
    height: "64px",
    backgroundColor: BRAND.white,
    borderBottom: `1px solid ${BRAND.border}`,
    display: "flex",
    alignItems: "center",
    padding: "0 2rem",
    gap: "2rem",
    boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
    [MEDIA.smDown]: { padding: "0 1rem", gap: "0.75rem" },
  },
  navLogo: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    textDecoration: "none",
    marginRight: "1rem",
    flexShrink: 0,
  },
  navLogoDot: {
    width: "28px",
    height: "28px",
    borderRadius: "8px",
    backgroundColor: BRAND.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  navLogoLeaf: {
    color: BRAND.secondary,
    fontSize: "16px",
    lineHeight: 1,
  },
  navLogoText: {
    fontSize: "1rem",
    fontWeight: 700,
    color: BRAND.textMain,
    fontFamily: "Inter, sans-serif",
    letterSpacing: "-0.01em",
  },
  navLinks: {
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
    flex: 1,
    [MEDIA.smDown]: { display: "none" },
  },
  burgerBtn: {
    display: "none",
    [MEDIA.smDown]: {
      display: "inline-flex",
      color: BRAND.textMain,
      padding: "6px",
    },
  },
  // Mobile drawer
  mobileDrawerPaper: {
    width: "82vw",
    maxWidth: "340px",
    backgroundColor: BRAND.white,
    display: "flex",
    flexDirection: "column",
  },
  mobileDrawerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.85rem 1rem",
    borderBottom: `1px solid ${BRAND.border}`,
  },
  mobileDrawerLinks: {
    display: "flex",
    flexDirection: "column",
    padding: "0.5rem",
    gap: "0.15rem",
  },
  mobileDrawerLink: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1rem",
    borderRadius: "10px",
    cursor: "pointer",
    fontFamily: "Inter, sans-serif",
    fontSize: "0.95rem",
    fontWeight: 500,
    color: BRAND.textMain,
    textDecoration: "none",
    transition: "background 0.12s",
    "&:hover": { backgroundColor: BRAND.background },
  },
  mobileDrawerLinkActive: {
    backgroundColor: BRAND.background,
    color: BRAND.primary,
    fontWeight: 700,
  },
  mobileDrawerLinkLaunch: {
    backgroundColor: BRAND.secondary,
    color: BRAND.primary,
    fontWeight: 700,
    "&:hover": { backgroundColor: "#3db83d", color: BRAND.white },
  },
  mobileDrawerSectionLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: BRAND.textMuted,
    padding: "0.75rem 1rem 0.35rem",
    fontFamily: "Inter, sans-serif",
  },
  navLink: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    padding: "0.45rem 1rem",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background 0.15s, color 0.15s",
    fontFamily: "Inter, sans-serif",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: BRAND.textMuted,
    border: "none",
    background: "transparent",
    "&:hover": {
      backgroundColor: BRAND.background,
      color: BRAND.textMain,
    },
  },
  navLinkActive: {
    color: `${BRAND.primary} !important`,
    fontWeight: 700,
    borderBottom: `2px solid ${BRAND.primary}`,
    borderRadius: 0,
    paddingBottom: "calc(0.45rem - 2px)",
    "&:hover": {
      backgroundColor: "transparent",
      color: `${BRAND.primary} !important`,
    },
  },
  navLinkLaunch: {
    backgroundColor: BRAND.secondary,
    color: `${BRAND.primary} !important`,
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#3db83d",
      color: `${BRAND.white} !important`,
    },
  },
  navRight: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    flexShrink: 0,
  },
  navRightAccount: {
    [MEDIA.smDown]: { display: "none" },
  },
  navRightDivider: {
    [MEDIA.smDown]: { display: "none" },
  },
  navRightName: {
    [MEDIA.smDown]: { display: "none" },
  },
  navRightAvatar: {
    [MEDIA.smDown]: { display: "none" },
  },
  navDivider: {
    width: "1px",
    height: "28px",
    backgroundColor: BRAND.border,
    margin: "0 0.5rem",
    flexShrink: 0,
  },
  accountSelect: {
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiSelect-select": {
      padding: "4px 28px 4px 10px !important",
      fontSize: "0.8rem",
      color: BRAND.textMuted,
      fontFamily: "Inter, sans-serif",
      fontWeight: 500,
    },
    "& .MuiSvgIcon-root": { fontSize: "1rem", color: BRAND.textMuted },
    backgroundColor: BRAND.background,
    borderRadius: "8px",
  },
  userName: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: BRAND.textMain,
    fontFamily: "Inter, sans-serif",
    whiteSpace: "nowrap",
    padding: "0 0.5rem",
  },
  avatar: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: BRAND.primary,
    color: BRAND.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8rem",
    fontWeight: 700,
    fontFamily: "Inter, sans-serif",
    flexShrink: 0,
    letterSpacing: "0.05em",
    cursor: "pointer",
    transition: "opacity 0.15s",
    "&:hover": { opacity: 0.85 },
  },
  menuItem: {
    fontFamily: "Inter, sans-serif",
    fontSize: "0.875rem",
    padding: "0.6rem 1.25rem",
    gap: "0.6rem",
    color: BRAND.textMain,
  },
  menuItemDanger: {
    fontFamily: "Inter, sans-serif",
    fontSize: "0.875rem",
    padding: "0.6rem 1.25rem",
    gap: "0.6rem",
    color: "#E84343",
  },

  // ── Content ──
  content: {
    marginTop: "64px",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },

  // ── Footer ──
  footer: {
    position: "sticky",
    bottom: 0,
    zIndex: 100,
    backgroundColor: BRAND.primary,
    padding: "1rem 2.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "2rem",
    fontFamily: "Inter, sans-serif",
    [MEDIA.smDown]: {
      position: "static",
      padding: "1rem 1rem",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "1rem",
    },
  },
  footerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    [MEDIA.smDown]: { gap: "0.75rem" },
  },
  footerCopy: {
    fontSize: "0.78rem",
    color: "rgba(255,255,255,0.5)",
    fontFamily: "Inter, sans-serif",
  },
  footerHelpSection: {
    display: "flex",
    alignItems: "center",
    gap: "2rem",
    [MEDIA.smDown]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "0.75rem",
      width: "100%",
    },
  },
  footerHelpTitle: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "1rem",
    fontWeight: 700,
    color: BRAND.white,
    fontFamily: "Inter, sans-serif",
    whiteSpace: "nowrap",
  },
  footerLinks: {
    display: "flex",
    gap: "1.5rem",
    flexWrap: "wrap",
  },
  footerLink: {
    fontSize: "0.78rem",
    color: "rgba(255,255,255,0.55)",
    fontFamily: "Inter, sans-serif",
    cursor: "pointer",
    textDecoration: "none",
    whiteSpace: "nowrap",
    "&:hover": { color: BRAND.white },
    transition: "color 0.15s",
  },
}));

// ─── Component ────────────────────────────────────────────────────────────────
export default function LightLayout({ children }) {
  const { classes, cx } = useStyles();
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const canCreateDossier = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER);
  const { authState, logoutKeycloak } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const accountId = useSelectedAccountId();

  const [userFullName, setUserFullName] = useState("");
  const [avatarAnchor, setAvatarAnchor] = useState(null);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (authState.user) {
      setUserFullName(`${authState.user.given_name || ""} ${authState.user.family_name || ""}`.trim());
    }
  }, [authState.user]);

  useEffect(() => {
    if (accountId && !state.contextModels) {
      getContextModel(accountId, dispatch).catch(() => {});
    }
  }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const contextModels = state.contextModels ?? [];
  const hasContexts = contextModels.length > 0;
  const handleAccountChange = (event) => {
    const accId = event.target.value;
    dispatch({ type: "SELECTED_ACCOUNT", account: state.accounts.find((acc) => acc.id === accId) });
    localStorage.setItem("SELECTED_ACCOUNT", accId);
    history.push(getUrlWithAccountId(accId, ROUTES.DASHBOARD));
  };

  const isActive = (route) => location.pathname.includes(`/${route}`);

  const getNavUrl = (route) => getUrlWithAccountId(accountId, route);
  const handleNav = (e, route) => {
    if (e.metaKey || e.ctrlKey || e.button === 1) return;
    e.preventDefault();
    history.push(getNavUrl(route));
  };

  // Logo
  const logo = state.personalization?.isCustomTemplate
    ? state.personalization?.isLogoImage && state.personalization?.logoURI
      ? <img src={state.personalization.logoURI} alt="logo" style={{ maxHeight: "32px" }} />
      : <Typography className={classes.navLogoText}>{state.personalization?.logoText}</Typography>
    : <img src="https://www.aprovall.com/wp-content/uploads/2024/11/aprovall.svg" alt="Aprovall" style={{ height: "28px" }} />;

  // User initials from Keycloak
  const userInitials = (() => {
    const u = authState.user;
    if (!u) return "?";
    const first = u.given_name?.[0] || "";
    const last = u.family_name?.[0] || "";
    return (first + last).toUpperCase() || u.email?.[0]?.toUpperCase() || "?";
  })();

  // Wait for language to be loaded before rendering (avoids English flash)
  if (!state.lang) return <LightLoader overlay />;

  return (
    <Box className={classes.root}>
      {/* ── Navbar ── */}
      <Box className={classes.navbar} component="nav">
        {/* Logo */}
        <Box component="a" href={getNavUrl(ROUTES.DASHBOARD)} className={classes.navLogo} onClick={(e) => handleNav(e, ROUTES.DASHBOARD)} style={{ cursor: "pointer", textDecoration: "none" }}>
          {logo}
        </Box>

        {/* Nav links */}
        <Box className={classes.navLinks}>
          {/* Accueil */}
          <Box
            component="a" href={getNavUrl(ROUTES.DASHBOARD)}
            className={cx(classes.navLink, isActive(ROUTES.DASHBOARD) && !isActive(ROUTES.DOSSIERS) && classes.navLinkActive)}
            onClick={(e) => handleNav(e, ROUTES.DASHBOARD)}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {i18next.t("light.navbar.home")}
          </Box>

          {/* Lancer une évaluation — toujours en vert vif */}
          {canCreateDossier && (
          <Box
            component="a" href={getNavUrl(ROUTES.LIGHT_LAUNCH)}
            className={cx(classes.navLink, classes.navLinkLaunch)}
            onClick={(e) => handleNav(e, ROUTES.LIGHT_LAUNCH)}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {i18next.t("light.navbar.launch")}
          </Box>
          )}

          {/* Mes fournisseurs */}
          <Box
            component="a" href={getNavUrl(ROUTES.LIGHT_THIRDPARTIES)}
            className={cx(classes.navLink, (isActive(ROUTES.LIGHT_THIRDPARTIES) || isActive('light/thirdparties')) && classes.navLinkActive)}
            onClick={(e) => handleNav(e, ROUTES.LIGHT_THIRDPARTIES)}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {i18next.t("light.navbar.thirdparties")}
          </Box>

          {/* Contextes */}
          {hasContexts && contextModels.length === 1 ? (
            <Box
              component="a" href={getNavUrl(ROUTES.LIGHT_CONTEXTS)}
              className={cx(classes.navLink, isActive(ROUTES.LIGHT_CONTEXTS) && classes.navLinkActive)}
              onClick={(e) => handleNav(e, ROUTES.LIGHT_CONTEXTS)}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {contextModels[0].name}
            </Box>
          ) : hasContexts ? (
            <Select
              value={selectedModelId ?? ""}
              onChange={(e) => {
                const mid = e.target.value;
                setSelectedModelId(mid);
                history.push(`${getNavUrl(ROUTES.LIGHT_CONTEXTS)}?modelId=${mid}`);
              }}
              displayEmpty
              variant="outlined"
              size="small"
              sx={{
                fontFamily: "Inter, sans-serif", fontSize: "0.875rem",
                fontWeight: isActive(ROUTES.LIGHT_CONTEXTS) ? 600 : 500,
                color: isActive(ROUTES.LIGHT_CONTEXTS) ? BRAND.primary : BRAND.textMuted,
                backgroundColor: isActive(ROUTES.LIGHT_CONTEXTS) ? "#EBF2EB" : "transparent",
                borderRadius: "8px",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BRAND.border },
                minWidth: "140px",
              }}
              MenuProps={{ PaperProps: { sx: { borderRadius: "10px", mt: 0.5 } } }}
            >
              <MenuItem value="" disabled sx={{ fontFamily: "Inter", fontSize: "0.8rem" }}>
                {i18next.t("light.contexts.selectModel")}
              </MenuItem>
              {contextModels.map((m) => (
                <MenuItem key={m.id} value={m.id} sx={{ fontFamily: "Inter", fontSize: "0.85rem" }}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
          ) : null}

          {/* Mes évaluations */}
          <Box
            component="a" href={getNavUrl(ROUTES.LIGHT_EVALUATIONS)}
            className={cx(classes.navLink, (isActive(ROUTES.LIGHT_EVALUATIONS) || isActive('light/dossiers')) && classes.navLinkActive)}
            onClick={(e) => handleNav(e, ROUTES.LIGHT_EVALUATIONS)}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {i18next.t("light.navbar.evaluations")}
          </Box>
        </Box>

        {/* Right: account selector | divider | name | avatar */}
        <Box className={classes.navRight}>
          {/* Sélecteur de compte */}
          {state.accounts?.length > 1 ? (
            <Select
              value={state.account?.id || ""}
              onChange={handleAccountChange}
              variant="outlined"
              className={cx(classes.accountSelect, classes.navRightAccount)}
            >
              {state.accounts.map((acc) => (
                <MenuItem key={acc.id} value={acc.id} style={{ fontSize: "0.8rem", fontFamily: "Inter" }}>
                  {acc.name}
                </MenuItem>
              ))}
            </Select>
          ) : state.account?.name ? (
            <Typography className={cx(classes.accountSelect, classes.navRightAccount)} style={{
              fontSize: "0.8rem", color: BRAND.textMuted, fontFamily: "Inter",
              backgroundColor: BRAND.background, borderRadius: "8px", padding: "4px 10px",
            }}>
              {state.account.name}
            </Typography>
          ) : null}

          {/* Séparateur */}
          <Box className={cx(classes.navDivider, classes.navRightDivider)} />

          {/* Nom complet */}
          {userFullName && (
            <Typography className={cx(classes.userName, classes.navRightName)}>{userFullName}</Typography>
          )}

          {/* Avatar */}
          <Box className={cx(classes.avatar, classes.navRightAvatar)} onClick={(e) => setAvatarAnchor(e.currentTarget)}>
            {userInitials}
          </Box>

          {/* Burger (mobile only) */}
          <IconButton
            className={classes.burgerBtn}
            onClick={() => setMobileOpen(true)}
            aria-label="menu"
            size="small"
          >
            <MenuIcon />
          </IconButton>

          {/* Dropdown menu */}
          <Menu
            anchorEl={avatarAnchor}
            open={Boolean(avatarAnchor)}
            onClose={() => setAvatarAnchor(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: "8px",
                minWidth: 180,
                borderRadius: "12px",
                border: `1px solid ${BRAND.border}`,
                boxShadow: "6px 6px 9px rgba(0,0,0,0.08)",
                overflow: "visible",
              },
            }}
          >
            {userFullName && (
              <Box sx={{ px: 2, py: 1.25 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", fontFamily: "Inter", color: BRAND.textMain }}>
                  {userFullName}
                </Typography>
              </Box>
            )}
            <Divider />
            <MenuItem
              className={classes.menuItem}
              onClick={() => {
                setAvatarAnchor(null);
                history.push(getUrlWithAccountId(accountId, ROUTES.PROFILE));
              }}
            >
              <ListItemIcon sx={{ minWidth: "28px !important" }}>👤</ListItemIcon>
              {i18next.t("profile")}
            </MenuItem>
            <MenuItem
              className={classes.menuItem}
              onClick={() => {
                setAvatarAnchor(null);
                history.push(getUrlWithAccountId(accountId, ROUTES.LIGHT_ACCOUNT));
              }}
            >
              <ListItemIcon sx={{ minWidth: "28px !important" }}>📋</ListItemIcon>
              {i18next.t("light.navbar.account")}
            </MenuItem>
            <MenuItem
              className={classes.menuItem}
              onClick={() => {
                setAvatarAnchor(null);
                setAppMode('advanced');
                window.location.replace(getUrlWithAccountId(accountId, ROUTES.DASHBOARD));
              }}
            >
              <ListItemIcon sx={{ minWidth: "28px !important" }}>⚙️</ListItemIcon>
              {i18next.t("light.navbar.advancedMode")}
            </MenuItem>
            <Divider />
            <MenuItem
              className={classes.menuItemDanger}
              onClick={() => {
                setAvatarAnchor(null);
                logoutKeycloak();
              }}
            >
              <ListItemIcon sx={{ minWidth: "28px !important" }}>🚪</ListItemIcon>
              {i18next.t("logout")}
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* ── Mobile drawer ── */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ className: classes.mobileDrawerPaper }}
      >
        <Box className={classes.mobileDrawerHeader}>
          <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Box className={classes.avatar} style={{ width: 32, height: 32, fontSize: "0.72rem" }}>
              {userInitials}
            </Box>
            {userFullName && (
              <Typography style={{ fontSize: "0.85rem", fontWeight: 600, color: BRAND.textMain, fontFamily: "Inter, sans-serif" }}>
                {userFullName}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setMobileOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Sélecteur de compte (mobile) */}
        {state.accounts?.length > 1 ? (
          <Box style={{ padding: "0.5rem 1rem" }}>
            <Select
              value={state.account?.id || ""}
              onChange={handleAccountChange}
              variant="outlined"
              fullWidth
              size="small"
              sx={{ fontSize: "0.85rem", fontFamily: "Inter, sans-serif", borderRadius: "10px" }}
            >
              {state.accounts.map((acc) => (
                <MenuItem key={acc.id} value={acc.id} style={{ fontSize: "0.85rem", fontFamily: "Inter" }}>
                  {acc.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        ) : state.account?.name ? (
          <Box style={{ padding: "0.5rem 1rem" }}>
            <Typography style={{
              fontSize: "0.8rem", color: BRAND.textMuted, fontFamily: "Inter, sans-serif",
              backgroundColor: BRAND.background, borderRadius: "10px", padding: "6px 10px",
            }}>
              {state.account.name}
            </Typography>
          </Box>
        ) : null}

        <Divider />

        {/* Nav links */}
        <Box className={classes.mobileDrawerLinks}>
          <Box
            component="a" href={getNavUrl(ROUTES.DASHBOARD)}
            className={cx(classes.mobileDrawerLink, isActive(ROUTES.DASHBOARD) && !isActive(ROUTES.DOSSIERS) && classes.mobileDrawerLinkActive)}
            onClick={(e) => handleNav(e, ROUTES.DASHBOARD)}
          >
            {i18next.t("light.navbar.home")}
          </Box>

          {canCreateDossier && (
            <Box
              component="a" href={getNavUrl(ROUTES.LIGHT_LAUNCH)}
              className={cx(classes.mobileDrawerLink, classes.mobileDrawerLinkLaunch)}
              onClick={(e) => handleNav(e, ROUTES.LIGHT_LAUNCH)}
            >
              {i18next.t("light.navbar.launch")}
            </Box>
          )}

          <Box
            component="a" href={getNavUrl(ROUTES.LIGHT_THIRDPARTIES)}
            className={cx(classes.mobileDrawerLink, (isActive(ROUTES.LIGHT_THIRDPARTIES) || isActive('light/thirdparties')) && classes.mobileDrawerLinkActive)}
            onClick={(e) => handleNav(e, ROUTES.LIGHT_THIRDPARTIES)}
          >
            {i18next.t("light.navbar.thirdparties")}
          </Box>

          {hasContexts && contextModels.length === 1 && (
            <Box
              component="a" href={getNavUrl(ROUTES.LIGHT_CONTEXTS)}
              className={cx(classes.mobileDrawerLink, isActive(ROUTES.LIGHT_CONTEXTS) && classes.mobileDrawerLinkActive)}
              onClick={(e) => handleNav(e, ROUTES.LIGHT_CONTEXTS)}
            >
              {contextModels[0].name}
            </Box>
          )}

          {hasContexts && contextModels.length > 1 && contextModels.map((m) => (
            <Box
              key={m.id}
              component="a" href={`${getNavUrl(ROUTES.LIGHT_CONTEXTS)}?modelId=${m.id}`}
              className={cx(classes.mobileDrawerLink, isActive(ROUTES.LIGHT_CONTEXTS) && selectedModelId === m.id && classes.mobileDrawerLinkActive)}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.button === 1) return;
                e.preventDefault();
                setSelectedModelId(m.id);
                history.push(`${getNavUrl(ROUTES.LIGHT_CONTEXTS)}?modelId=${m.id}`);
              }}
            >
              {m.name}
            </Box>
          ))}

          <Box
            component="a" href={getNavUrl(ROUTES.LIGHT_EVALUATIONS)}
            className={cx(classes.mobileDrawerLink, (isActive(ROUTES.LIGHT_EVALUATIONS) || isActive('light/dossiers')) && classes.mobileDrawerLinkActive)}
            onClick={(e) => handleNav(e, ROUTES.LIGHT_EVALUATIONS)}
          >
            {i18next.t("light.navbar.evaluations")}
          </Box>
        </Box>

        <Divider style={{ margin: "0.5rem 0" }} />

        {/* Compte */}
        <Box className={classes.mobileDrawerLinks}>
          <Box
            component="a" href={getUrlWithAccountId(accountId, ROUTES.PROFILE)}
            className={classes.mobileDrawerLink}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.button === 1) return;
              e.preventDefault();
              history.push(getUrlWithAccountId(accountId, ROUTES.PROFILE));
            }}
          >
            <span>👤</span> {i18next.t("profile")}
          </Box>
          <Box
            component="a" href={getUrlWithAccountId(accountId, ROUTES.LIGHT_ACCOUNT)}
            className={classes.mobileDrawerLink}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.button === 1) return;
              e.preventDefault();
              history.push(getUrlWithAccountId(accountId, ROUTES.LIGHT_ACCOUNT));
            }}
          >
            <span>📋</span> {i18next.t("light.navbar.account")}
          </Box>
          <Box
            className={classes.mobileDrawerLink}
            onClick={() => {
              setAppMode('advanced');
              window.location.replace(getUrlWithAccountId(accountId, ROUTES.DASHBOARD));
            }}
          >
            <span>⚙️</span> {i18next.t("light.navbar.advancedMode")}
          </Box>
          <Box
            className={classes.mobileDrawerLink}
            style={{ color: "#E84343" }}
            onClick={() => logoutKeycloak()}
          >
            <span>🚪</span> {i18next.t("logout")}
          </Box>
        </Box>
      </Drawer>

      {/* ── Content ── */}
      <Box className={classes.content}>
        {state.account?.isSuspended ? (
          <Box style={{ display: "flex", flexDirection: "column", width: "100%" }}>
            {/* Hero */}
            <Box style={{
              width: "100%", padding: "3rem 2rem 4rem",
              backgroundColor: BRAND.primary,
              backgroundImage: "url(https://www.aprovall.com/wp-content/uploads/2024/11/texture.webp)",
              backgroundSize: "cover", backgroundPosition: "center",
              display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
            }}>
              <Typography style={{ fontSize: "clamp(1.3rem, 0.87rem + 1.5vw, 1.6rem)", fontWeight: 700, color: BRAND.white, fontFamily: "Inter, sans-serif" }}>
                {i18next.t("light.suspended.title")}
              </Typography>
            </Box>
            {/* Card */}
            <Box style={{ maxWidth: "520px", width: "100%", margin: "-2rem auto 0", padding: "0 1.5rem", boxSizing: "border-box" }}>
              <Box style={{
                backgroundColor: BRAND.white, borderRadius: "16px", border: `1.5px solid ${BRAND.border}`,
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)", padding: "2.5rem 2rem", textAlign: "center",
                display: "flex", flexDirection: "column", alignItems: "center", gap: "1.25rem",
              }}>
                {/* Illustration */}
                <Box style={{
                  width: "72px", height: "72px", borderRadius: "50%",
                  backgroundColor: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                  </svg>
                </Box>
                <Typography style={{ fontSize: "1.1rem", fontWeight: 700, color: BRAND.textMain, fontFamily: "Inter, sans-serif" }}>
                  {i18next.t("light.suspended.message")}
                </Typography>
                <Typography style={{ fontSize: "0.875rem", fontWeight: 500, color: BRAND.textMuted, fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>
                  {i18next.t("light.suspended.detail")}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : children}
      </Box>

      {/* ── Footer ── */}
      <Box className={classes.footer} component="footer">
        {/* Left: logo + copyright */}
        <Box className={classes.footerLeft}>
          <img
            src="https://www.aprovall.com/wp-content/uploads/2024/11/aprovall.svg"
            alt="Aprovall"
            style={{ height: "22px", filter: "brightness(0) invert(1)", opacity: 0.9 }}
          />
          <Typography className={classes.footerCopy}>
            © {new Date().getFullYear()} — {i18next.t("light.footer.copyright")}
          </Typography>
        </Box>

        {/* Right: help links */}
        <Box className={classes.footerHelpSection}>
          <Box className={classes.footerHelpTitle}>
            <span style={{
              width: "26px", height: "26px", borderRadius: "50%",
              border: "2px solid rgba(255,255,255,0.6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.95rem", fontWeight: 700, color: "rgba(255,255,255,0.85)", flexShrink: 0,
            }}>?</span>
            {i18next.t("light.footer.help")}
          </Box>
          <Box className={classes.footerLinks}>
            <a
              href="https://www.aprovall.com/fr/aprovall-documentation/"
              target="_blank"
              rel="noreferrer"
              className={classes.footerLink}
            >
              {i18next.t("light.footer.privacy")}
            </a>
            <a
              href="https://trust.aprovall.com/"
              target="_blank"
              rel="noreferrer"
              className={classes.footerLink}
            >
              {i18next.t("light.footer.trustCenter")}
            </a>
            <a
              href="https://www.aprovall.com/fr/aprovall-documentation/"
              target="_blank"
              rel="noreferrer"
              className={classes.footerLink}
            >
              {i18next.t("light.footer.terms")}
            </a>
          </Box>
        </Box>
      </Box>

      <EASnackBar />
    </Box>
  );
}
